import React, {
    FC, MutableRefObject, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import queryString from 'query-string';

/* CONTEXTS */
import { MenuDataContext } from 'app/containers/contexts/menu.context';

/* UTILS */
import MenuDishesProvider from 'app/containers/MenuDishesProvider';
import { getCategoryCount } from 'app/utils/filters';
import { analyticService } from 'global/services';

/* DATA */
import {
    CATEGORY_MAIN,
    CATEGORY_MOST_INTRESTING,
} from 'app/const/categories';

/* COMPONENTS */
import debounce from 'lodash/debounce';
import { useDisabledDatesSelected } from 'app/hooks';
import { useParticipantIdABTest } from 'app/views/Menu/Menu/hooks/useParticipantIdABTest';
import classNames from 'classnames';
import MobileCategoryTab from '../MobileCategoryTab/MobileCategoryTab';

/* STYLES */
import css from './MobileCategoriesSlider.module.scss';
import { useAvailableCategories } from '../../hooks/useAvailableCategories';


/* TYPES */
type TypeCategory = {
    id: string,
    title: string,
}

const scrollingDispatcherTimeout: number = 700;
const categoryChangeEventTimeout: number = 200;

export const MobileCategoriesSlider: FC = () => {
    /* STATE */
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(CATEGORY_MAIN);
    const activeCategoryIdChangedBy: MutableRefObject<'menu' | 'tab'> = useRef<'menu' | 'tab'>('menu');
    const sliderRef: RefObject<HTMLDivElement> = useRef(null);


    const { scrolling: { categoryId } } = useContext(MenuDataContext);

    const isDisabledDateSelected = useDisabledDatesSelected();
    const [
        filteredCategories,
        dishesProvidedData,
    ] = useAvailableCategories();


    const { location: { pathname, search } } = window;

    const params = queryString.parse(search);

    const isProfilePage = useMemo(
        () => pathname.includes('profile'),
        [pathname],
    );

    /* HANDLERS */
    const changeScrollingDispatcher = (origin: 'menu' | 'tab'): void => {
        if (activeCategoryIdChangedBy.current === origin) return;
        activeCategoryIdChangedBy.current = origin;
    };

    const handleClickOnCategory = useRef(
        debounce(
            (id: string): void => {
                changeScrollingDispatcher('tab');
                setActiveCategoryId(id);

                setTimeout(() => {
                    changeScrollingDispatcher('menu');
                }, scrollingDispatcherTimeout);

                const categories = [
                    {
                        id: CATEGORY_MOST_INTRESTING,
                        title: 'Новое',
                    },
                    // @ts-ignore
                    ...dishesProvidedData.categories,
                ];
                const categoryTitle = categories.find((e: TypeCategory) => e.id === id)?.title ?? '';
                analyticService
                    .push({
                        eventName: 'Change_Filter_Item',
                        itemKey: 'category',
                        action: 'on',
                        categoryId: id,
                    })
                    .push({
                        eventName: 'Filter_Change_Category',
                        filterValue: categoryTitle,
                        eventAction: 'Change',
                        eventLabel: 'Category',
                    });

                const chosenFiltersCachedData = sessionStorage.getItem('chosenFilters') ?? '';
                if (!chosenFiltersCachedData?.includes(categoryTitle)) {
                    sessionStorage.setItem(
                        'chosenFilters',
                        `${chosenFiltersCachedData ? `${chosenFiltersCachedData} ` : ''}${categoryTitle}`,
                    );
                }
            },
            scrollingDispatcherTimeout,
            { leading: true, trailing: false },
        ),
    );

    const scrollToActiveTab = useCallback(
        (id: string): void => {
            const sliderContainer = sliderRef.current;
            const tabs = Array.from(sliderContainer?.children || []) as HTMLElement[];
            const tab = tabs.find((e) => e.dataset.category_id === id) as HTMLElement;

            if (!tab || !sliderContainer) return;

            const scrollPadding = document.documentElement.clientWidth < 375 ? 16 : 20;
            const tabLeftPosition = tab.offsetLeft;
            sliderContainer.scrollTo({
                left: tabLeftPosition - scrollPadding,
                behavior: 'smooth',
            });
        },
        [],
    );

    const handleActiveTabChange = useCallback(
        () => {
            if (!activeCategoryId) return;
            scrollToActiveTab(activeCategoryId);
            if (activeCategoryIdChangedBy.current === 'menu') return;
            const categoryChangeEvent = new CustomEvent('categoryChangeFromSlider', {
                bubbles: true,
                detail: {
                    activeCategoryId,
                },
            });
            setTimeout(() => {
                document.dispatchEvent(categoryChangeEvent);
            }, categoryChangeEventTimeout);
        },
        [activeCategoryId, scrollToActiveTab],
    );

    /* EFFECTS */
    useEffect(() => {
        if (categoryId) {
            setActiveCategoryId(categoryId);
        }
    }, [categoryId]);

    useEffect(
        () => {
            handleActiveTabChange();
        },
        [
            activeCategoryId,
            activeCategoryIdChangedBy,
            handleActiveTabChange,
        ],
    );

    useEffect(
        () => {
            if (params?.mc) {
                scrollToActiveTab(params?.mc as string);
            }
        },
        [params?.mc, scrollToActiveTab],
    );

    if (isDisabledDateSelected || isProfilePage) {
        return null;
    }

    const sliderWrapperClasses = classNames({
        [css.categoriesSliderWrapper]: true,
        [css.newLayout]: true,
    });

    return (
        <MenuDishesProvider
            wrapper="MobileCategoriesSlider"
            // @ts-expect-error
            menuFilter={dishesProvidedData.menuFilter}
            render={({ menuDishesQuery: { basketDishes, menuDishes: allDishes } }) => {
                if (!filteredCategories.length) return null;

                return (
                    <div
                        className={sliderWrapperClasses}
                        ref={sliderRef}
                        data-active_category_id={activeCategoryId}
                        id="categoriesSliderWrapper"
                    >
                        { /* @ts-ignore */}
                        {filteredCategories.map((c: any) => {
                            const count = getCategoryCount({
                                id: c.id,
                                menuDishes: allDishes,
                                selectedTagIds: dishesProvidedData.selectedTags,
                                basketDishes,
                            });
                            if (dishesProvidedData.selectedTags.length === 0 && count === 0) {
                                return null;
                            }

                            return (
                                <div data-category_id={c.id} key={c.id}>
                                    <MobileCategoryTab
                                        onClick={handleClickOnCategory.current}
                                        category={c}
                                        isActive={activeCategoryId === c.id}
                                        newLayout
                                    />
                                </div>
                            );
                        })}
                    </div>
                );
            }}
        />
    );
};
